import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, Optional, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../mat-account/confirm-dialog/confirm-dialog.component';
import { Validators, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { HelperMethods } from '../../common/helper-methods.service';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { SharedService } from '../../../shared/services/shared.service';
import { MatTabGroup } from '@angular/material/tabs';
import { ToastrService } from 'ngx-toastr';
import { DtoSchool } from '../../../models/school.model';
import * as XLSX from 'xlsx';
import { MatDialogCloseEvent } from '../../../const';
import { SchoolService } from '../../school/school.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { Users } from '../../../models/user-center.model';
import { RoleMaster } from '../create-user/user-details/user-details.component';

interface UploadFileList {
  id: string | null;
  files: File[];
}

@Component({
  selector: 'app-add-school',
  templateUrl: './add-school.component.html'
})
export class AddSchoolComponent implements OnInit, OnDestroy {
  @ViewChildren('schoolFileInput') schoolFileInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('schoolAdminFileInput') schoolAdminFileInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChild('uploadFileInput') uploadFileInput: ElementRef | undefined;
  @ViewChild('tabGroup', { static: false }) tabGroup: MatTabGroup | undefined;
  @Input() isEdit: boolean = false;
  @Input() schoolData: DtoSchool[] = [];
  @Input() isIndividualEntity: boolean = false;
  @Input() assignedAccountAdminDetails: Users = {} as Users;
  @Input() accountAdminList: Users[] = [];
 
  selectedFile: File | null = null;
  excelData: DtoSchool[] = [];
  templateUrl: string = "";
  schoolAccountUploadFileList: { [index: number]: UploadFileList } = {};
  schoolAdminUploadFileLists: { [index: number]: UploadFileList } = {};
  maxSize: number = 1 * 1024 * 1024; // 1 MB in bytes
  subscription: Subscription = new Subscription();
  currentFormIndex: number = 0;
  selectedSchoolFileCount: number[] = [];
  selectedSchoolAdminFileCount: number[] = [];
  phoneNumberUtil = PhoneNumberUtil.getInstance();
  schoolDetailsForm = this.fb.group({
    schools: this.fb.array([])
  });
  userRole: string[] = [];

  constructor( public dialog: MatDialog, private helperMethods: HelperMethods, 
               private fb: FormBuilder, private sharedService: SharedService,
               private toasterService: ToastrService, private schoolService: SchoolService,
               @Optional() public dialogRef: MatDialogRef<AddSchoolComponent>,
               @Inject(MAT_DIALOG_DATA) public data: any ) {}

  ngOnInit(): void {
    this.userRole = sessionStorage.getItem('user_role')?.split(',')!;
    if(this.data.isEdit){
      this.patchSchool(this.data.school);
    }
    else if(this.isEdit){
      this.patchSchool(this.schoolData);
    }
    else{
      this.resetForm();
      this.addSchool(true);
    }
    this.sharedService.schoolFormValidity.subscribe(schoolForm => {
      if(schoolForm.controls['schools']){
        this.schoolDetailsForm = schoolForm;
      }
    });
    this.schoolDetailsForm.statusChanges.subscribe(status => {
      this.sharedService.updateSchoolFormValidity(this.schoolDetailsForm);
    });
    setTimeout(() => {
      this.sharedService.updateSchoolFormValidity(this.schoolDetailsForm);
    });
    this.subscription.add(this.sharedService.doFileUpload.subscribe((data) => {
      if(data){
        this.uploadFromLocal(data, this.currentFormIndex, true);
      }
      else{
        this.SchoolAdminuploadFromLocal(data, this.currentFormIndex, true);
      }
    }));
  }

  resetForm() {
    // Reset the form values
    this.schoolDetailsForm.reset();
  
    // Clear the FormArray 'schools'
    const schoolsDetailsArray = this.schoolDetailsForm.get('schools') as FormArray;
    while (schoolsDetailsArray && schoolsDetailsArray.length !== 0) {
      schoolsDetailsArray.removeAt(0); // Remove all controls one by one
    }
    this.sharedService.updateSchoolFormValidity(this.schoolDetailsForm);
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  cancel(){
    this.dialogRef.close({event: MatDialogCloseEvent.CANCELLED});
  }

  uploadFile(){
    if (this.uploadFileInput) {
      this.uploadFileInput.nativeElement.click();
    }
  }

  clearSchoolForm(){
    this.sharedService.resetSchoolForm();
    
    this.schoolAccountUploadFileList = [];
    this.schoolAdminUploadFileLists = [];
    this.selectedSchoolFileCount = [];
    this.selectedSchoolAdminFileCount = [];
  }

  updateSchool(){
    this.dialogRef.close({event: MatDialogCloseEvent.SUCCESS, data: this.schools.at(0).value});
  }

  loadExcelData(event: any) {
    this.sharedService.removeInvalidSchool();
    let file = event.target.files[0];
    let fileReader = new FileReader();
    fileReader.readAsBinaryString(file);
    fileReader.onload = (e) => {
      var workBook = XLSX.read(fileReader.result, {type: 'binary'});
      var sheetNames = workBook.SheetNames;
      this.excelData = XLSX.utils.sheet_to_json(workBook.Sheets[sheetNames[0]]);
      if (this.excelData.length === 0) {
        this.toasterService.warning('Uploaded Excel file (Sheet No: 1) is empty or does not contain valid data');
        this.addSchool(true);
        return;
      }
      const lowerCase = (str: string | any[]) => str[0].toLowerCase() + str. slice(1);
      const res = this.excelData.map(
        obj => Object.fromEntries(
          Object.entries(obj).map(
            ([k, v]) => {
              if (k === 'Id(URN/DfE Number/UKPRN)') {
                return ['schoolRegistrationId', v.toString()];
              } 
              else if (k === 'SchoolCode') {
                return ['id', v.toString()];
              }
              else {
                return [lowerCase(k), v];
              }
            }
          )
        )
      );
      this.excelData = res;
      let schoolNames = this.excelData.filter(x => x.schoolName !== undefined && x.schoolName !== '').map(x=>{return x.schoolName});
      if(schoolNames.length == 0){
        this.toasterService.warning("Uploaded excel is incorrect. Please try again with a valid excel");
        this.addSchool(true);
      }
      else{
        this.toasterService.info("Uploaded details of " + this.excelData.map(x=>{return x.schoolName}).join(',') + " school");
        this.patchSchool(this.excelData);
      }
      event.target.value = null;
    }
  }

  openConfirmDialog(event: Event): void {
    event.preventDefault();
    this.dialog.open(ConfirmDialogComponent , {width: '675px'});
  }

  getFilesForSchool(index: number): File[] {
    const schoolUploadFileList = this.schoolAccountUploadFileList[index];
    return schoolUploadFileList ? schoolUploadFileList.files || [] : [];
  }

  getFilesForAdmin(index: number): File[] {
    const schooladminUploadFileList = this.schoolAdminUploadFileLists[index];
    return schooladminUploadFileList ? schooladminUploadFileList.files || [] : [];
  }

  uploadFromLocal(event: Event, index: number, triggeredManually = false): void {
    if (!triggeredManually) {
      event.preventDefault();
      this.sharedService.setUploadedFile(this.getFilesForSchool(index));
      this.dialog.open(ConfirmDialogComponent, { width: '675px', data: {
        hideUpload: false,
        isParentUpload: true
      }}).afterClosed().subscribe(() => {
        this.selectedSchoolFileCount[index] = this.getFilesForSchool(index).length;
      });

      this.currentFormIndex = index;
    }

    const fileInput = this.schoolFileInputs.toArray()[index];
    if (fileInput  && index === this.currentFormIndex) {
      fileInput.nativeElement.click();
    }
  }

  SchoolAdminuploadFromLocal(event: Event, index: number, triggeredManually = false): void {
    if (!triggeredManually) {
      event.preventDefault();
      this.sharedService.setUploadedFile(this.getFilesForAdmin(index));
      this.dialog.open(ConfirmDialogComponent, { width: '675px', data: {
        hideUpload: false,
        isParentUpload: false
      }}).afterClosed().subscribe(() => {
        this.selectedSchoolAdminFileCount[index] = this.getFilesForAdmin(index).length;
      });

      this.currentFormIndex = index;
    }
    const fileInput = this.schoolAdminFileInputs.toArray()[index];
    if (fileInput) {
      fileInput.nativeElement.click();
    }
  }

  onFileSelected(event: any, index: number) {
    if (!this.schoolAccountUploadFileList[index]) {
      this.schoolAccountUploadFileList[index] = { id: null, files: [] };
    }

    let filesExist = event.target.files.length;
    if (filesExist > 0) {
      for (let i = 0; i < filesExist; i++) {
        if (event.target.files && event.target.files[i]) {
          if(event.target.files[i].size <= this.maxSize){
            let _object: File = event.target.files[i];

            this.schoolAccountUploadFileList[index].files.push(_object);
            const schoolFormGroup = this.schools.at(index) as FormGroup;
            schoolFormGroup.get('schoolAccountUploadFileList')?.setValue(this.schoolAccountUploadFileList[index].files);
          }
          else{
            this.toasterService.warning('File size must be less than 1 MB.');
          }
        }
      }
      this.sharedService.changeUploadingStatus(false);
      this.sharedService.setUploadedFile(this.schoolAccountUploadFileList[index].files);
      this.selectedSchoolFileCount[index] = this.schoolAccountUploadFileList[index].files.length;

      // Clear the file input value
      if (this.schoolFileInputs.toArray()[index]) {
        this.schoolFileInputs.toArray()[index].nativeElement.value = "";
      }
    }
  }

  onSchoolAdminFileSelected(event: any, index: number) {
    if (!this.schoolAdminUploadFileLists[index]) {
      this.schoolAdminUploadFileLists[index] = { id: null, files: [] };
    }

    let filesExist = event.target.files.length;
    if (filesExist > 0) {
      for (let i = 0; i < filesExist; i++) {
        if (event.target.files && event.target.files[i]) {
          if(event.target.files[i].size <= this.maxSize){
            let _object: File = event.target.files[i];

            this.schoolAdminUploadFileLists[index].files.push(_object);
            const schoolFormGroup = this.schools.at(index) as FormGroup;
            schoolFormGroup.get('schoolAdminUploadFileLists')?.setValue(this.schoolAdminUploadFileLists[index].files);
          }
          else{
            this.toasterService.warning('File size must be less than 1 MB.');
          }
        }
      }
      this.sharedService.changeUploadingStatus(false);
      this.sharedService.setUploadedFile(this.schoolAdminUploadFileLists[index].files);
      this.selectedSchoolAdminFileCount[index] = this.schoolAdminUploadFileLists[index].files.length;

      // Clear the file input value
      if (this.schoolAdminFileInputs.toArray()[index]) {
        this.schoolAdminFileInputs.toArray()[index].nativeElement.value = "";
      }
    }
  }
  
  addSchool(firstLoad: boolean = false, schoolList: DtoSchool[] = []){
    if(!firstLoad && (this.schoolDetailsForm.invalid || this.schoolDetailsForm.untouched)){
      this.schoolDetailsForm.markAllAsTouched();
      this.toasterService.warning("Please fill the required fields to proceed");
      return;
    }
    if(schoolList.length){
      schoolList.forEach((data: DtoSchool) => {
        let accountAdmin: any = this.accountAdminList.find(x => x.userId == data.assignedAccountAdmin)!;
        let newForm = this.getSchoolForm();
        let formattedNumber: string = "";
        if(data.schoolAdmin){
          let examplePhoneNumber = `+${data.schoolAdmin.countryCode?.toString()!}${data.schoolAdmin.phoneNumber?.toString()!}`;
          formattedNumber = this.phoneNumberUtil.format(
            this.phoneNumberUtil.parse(examplePhoneNumber),
            PhoneNumberFormat.INTERNATIONAL
          );
        }
        newForm.patchValue({
          schoolId: data.schoolId,
          id: data.id,
          assignedAccountAdmin: accountAdmin,
          schoolName: data.schoolName,
          location: data.location,
          numberOfSchoolUser: data.numberOfSchoolUser,
          schoolCapacity: data.schoolCapacity,
          headcountOfSchool: data.headcountOfSchool,
          schoolType: data.schoolType,
          numberOfNonTeachingStaff: data.numberOfNonTeachingStaff,
          numberOfTeachingStaff: data.numberOfTeachingStaff,
          curriculum: data.curriculum,
          address: data.address,
          localAuthority: data.localAuthority,
          schoolRegistrationId: data.schoolRegistrationId,
          notes: data.notes,
          schoolAdmin: {
            schoolAdminId: data.schoolAdmin?.schoolAdminId,
            schoolId: data.schoolAdmin?.schoolId,
            title: data.schoolAdmin?.title,
            firstName: data.schoolAdmin?.firstName,
            lastName: data.schoolAdmin?.lastName,
            emailId: data.schoolAdmin?.emailId,
            contactNumber: formattedNumber || ""
          },
          schoolUsers: []
        });
        if(!this.isIndividualEntity && this.assignedAccountAdminDetails.userId){
          newForm.get('assignedAccountAdmin')?.setValue(this.assignedAccountAdminDetails);
          newForm.get('assignedAccountAdmin')?.disable();
        }
        this.schools.push(newForm);
      });
    }
    else{
      let schoolForm = this.getSchoolForm();
      this.schools.push(schoolForm);
      if (this.userRole.includes(RoleMaster.AccountAdmin) && this.accountAdminList.length) {
        let assignedAccountAdmin: Users = this.accountAdminList[0];
        const schoolFormGroup = this.schools.at(0) as FormGroup;
        schoolFormGroup.get('assignedAccountAdmin')?.setValue(assignedAccountAdmin);
        schoolFormGroup.get('assignedAccountAdmin')?.disable();
      }
      else{
        if(!this.isIndividualEntity){
          const schoolFormGroup = this.schools.at(0) as FormGroup;
          schoolFormGroup.get('assignedAccountAdmin')?.setValue(this.assignedAccountAdminDetails);
          schoolFormGroup.get('assignedAccountAdmin')?.disable();
        }
      }
    }
    setTimeout(() => {
      if(this.tabGroup){
        this.tabGroup.selectedIndex! = this.schools.length - 1;
      }
    });
  }

  downloadFile(): void {
    const blobName = 'TemplateForBulkUploadSchool.xlsx'; // Specify the file name you want to download
    this.schoolService.downloadFile(blobName).subscribe((response: any) => {

      var url = response.blobUrl.toString();
      for (var i = 0; i < url.length; i++) {
        url = url.replace("~", "");
        url = url.replace("//", "%2F%2F");
        url = url.replace("/", "%2F");
        url = url.replace(":", "%3A");
    }
    console.log(url);
     var ViewDocumentUrl = "https://view.officeapps.live.com/op/view.aspx?src=" + url;
     window.open(ViewDocumentUrl, "_blank");
    },(err: HttpErrorResponse)=>{
      if(err.error){
        this.toasterService.error(err.error, "Error");
      }
    });
  }

  getSchoolForm(){
    const schoolForm = this.fb.group ({
      schoolId: [null as number | null],
      id: [''],
      assignedAccountAdmin: [{} as Users],
      schoolName: ['', [Validators.required, Validators.pattern('^.*[a-zA-Z]+.*$ ?')]],
      location: ['', [Validators.required, Validators.pattern('^.*[a-zA-Z]+.*$ ?')]],
      numberOfSchoolUser: [null as number | null, [Validators.required, Validators.pattern("^[0-9]*$")]],
      schoolCapacity: [null as number | null, [Validators.required, Validators.pattern("^[0-9]*$")]],
      headcountOfSchool: [null as number | null, [Validators.required, Validators.pattern("^[0-9]*$")]],
      schoolType: ['', [Validators.required, Validators.pattern('^.*[a-zA-Z]+.*$ ?')]],
      numberOfNonTeachingStaff: [null as number | null, [Validators.required, Validators.pattern("^[0-9]*$")]],
      numberOfTeachingStaff: [null as number | null, [Validators.required, Validators.pattern("^[0-9]*$")]],
      curriculum: ['', [Validators.required, Validators.pattern('^.*[a-zA-Z]+.*$ ?')]],
      address: ['', [Validators.required, Validators.pattern('^.*[a-zA-Z]+.*$ ?')]],
      localAuthority: ['', [Validators.required, Validators.pattern('^.*[a-zA-Z]+.*$ ?')]],
      schoolRegistrationId: ['', [Validators.required]],
      notes: [''],
      schoolAccountUploadFileList: this.fb.control([]),
      schoolAdminUploadFileLists: this.fb.control([]),
      schoolUsers : this.fb.array([]),
      schoolAdmin: this.fb.group({
        schoolAdminId: [null as number | null],
        schoolId: [null as number | null],
        title: [''],
        firstName: ['', [
          Validators.pattern('^.*[a-zA-Z]+.*$ ?'),
        ]],
        lastName: ['', [
          Validators.pattern('^.*[a-zA-Z]+.*$ ?'),
        ]],
        emailId: ['', [
          Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"),
        ]],
        contactNumber: ['', [
          this.helperMethods.PhoneNumberValidator(),
        ]]
      })
    });
    return schoolForm;
  }

  patchSchool(data: DtoSchool[]) {
    if(data.length){
      this.addSchool(true, data);
    }
  }

  removeSchool(index: number) {
    const currentIndex = this.tabGroup!.selectedIndex;
    this.schools.removeAt(index);

    // Adjust the selected tab index after deletion
    if (index === currentIndex) {
      this.tabGroup!.selectedIndex = Math.max(0, currentIndex - 1);
    } else if (index < currentIndex!) {
      this.tabGroup!.selectedIndex = currentIndex! - 1;
    }
  }

  get schools(): FormArray {
    return this.schoolDetailsForm.get('schools') as FormArray;
  }

}
